// ---------------------------------------------------------------------------
// Widget types
// ---------------------------------------------------------------------------
// analytics_widget_definition.widget_type: kpi | table | chart only.
// 'section' is a client-side layout primitive — never from catalog/preview/query.

export const WIDGET_TYPE = {
  KPI: 'kpi',
  CHART: 'chart',
  TABLE: 'table',
  SECTION: 'section',
} as const;

export const API_WIDGET_TYPES = [
  WIDGET_TYPE.KPI,
  WIDGET_TYPE.CHART,
  WIDGET_TYPE.TABLE,
] as const;
export type ApiWidgetType = typeof API_WIDGET_TYPES[number];

export const WIDGET_TYPES = [...API_WIDGET_TYPES, WIDGET_TYPE.SECTION] as const;
export type WidgetType = typeof WIDGET_TYPES[number];

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
// Frontend canvas model. Stored as-is in backend layout_json (opaque blob).
// Backend returns whatever was stored — no parsing or transformation.

export const ROW_TYPE = {
  KPI: 'kpi',
  VIZ: 'viz',
  SECTION: 'section',
} as const;
export type RowType = typeof ROW_TYPE[keyof typeof ROW_TYPE];

export type Row = {
  id: string; // stable UUID, generated once on creation
  type: RowType;
  tiles: string[]; // ordered placement_ids (empty for section rows)
  label?: string; // section rows only
};

export type Layout = Row[];

// Row capacity: kpi=4, viz=2, section=0 (enforced by store)

// ---------------------------------------------------------------------------
// Dashboard status
// ---------------------------------------------------------------------------

export const DASHBOARD_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
} as const;
export type DashboardStatus =
  typeof DASHBOARD_STATUS[keyof typeof DASHBOARD_STATUS];

// ---------------------------------------------------------------------------
// User reference — all created_by / updated_by fields
// ---------------------------------------------------------------------------

export type UserRef = {
  id: string;
  name: string;
  is_sense?: boolean; // frontend extension — not in backend type
};

// ---------------------------------------------------------------------------
// Canvas widget model (frontend store)
// ---------------------------------------------------------------------------

export type WidgetConfig = {
  widget_id: string; // placement_id
  widget_type: WidgetType;
  widget_key: string;
  label: string;
  widget_icon?: string | null;
  widget_filters_json: Record<string, unknown>;
  widget_config_json: Record<string, unknown>;
};

// ---------------------------------------------------------------------------
// Date range
// ---------------------------------------------------------------------------

export type DateRange = {
  startDate: string;
  endDate: string;
  timezone: string;
};

// ---------------------------------------------------------------------------
// Filter definitions
// ---------------------------------------------------------------------------

export type FilterType =
  | 'date_range_start'
  | 'date_range_end'
  | 'select'
  | 'text'
  | 'number';

export type FilterDefinition = {
  key: string;
  label: string;
  filter_type: FilterType;
  selection_mode: 'single' | 'multi';
  value_type: 'string' | 'date' | 'number';
  default_operator: string | null;
  static_options: {key: string; value: string}[] | null;
  options_query: string | null;
};

export type FilterOption = {key: string; value: string};

// ---------------------------------------------------------------------------
// Widget catalog
// ---------------------------------------------------------------------------

export type CatalogWidget = {
  widget_key: string;
  widget_type: ApiWidgetType;
  name: string;
  description?: string;
  product_area: string | null;
  // populated lazily via GET /widgets/:widget_key — not in list response
  supported_filters?: string[];
  supported_config?: string[];
};

export type CatalogGroup = {
  label: string;
  widgets: CatalogWidget[];
};

// ---------------------------------------------------------------------------
// Reports library (paginated widget list)
// ---------------------------------------------------------------------------

export type ReportListItem = {
  id: string;
  widget_key: string;
  name: string;
  description: string;
  widget_type: ApiWidgetType;
  product_area: string | null;
  widget_icon: string | null;
  is_global: boolean;
  agency_ids: string[];
  agency_names: string[];
  created_by: UserRef | null;
  updated_by: UserRef | null;
  usage_count: number;
};

export type ReportListResponse = {
  widgets: ReportListItem[];
  pagination: {total_count: number; page_size: number; offset: number};
};

// ---------------------------------------------------------------------------
// Dashboard list
// ---------------------------------------------------------------------------

export type DashboardListItem = {
  id: string;
  name: string;
  description: string | null;
  global_filters: Record<string, string | string[]>;
  is_default: boolean;
  created_by: UserRef | null;
  updated_by: UserRef | null;
  time_created: string | null;
  // Not in DashboardSummary spec but returned by some backends; used as
  // a display fallback: time_updated ?? time_created
  time_updated?: string | null;
};

export type DashboardListResponse = {
  dashboards: DashboardListItem[];
  pagination: {total_count: number; page_size: number; offset: number};
};

// ---------------------------------------------------------------------------
// Dashboard detail (GET /dashboards/:id)
// ---------------------------------------------------------------------------

// Placement as returned by the API — widget_type lives inside widget_def only.
// Per-placement overrides (label, widget_icon, widget_type) are stored inside
// the `widget_config` JSON blob and extracted at hydration time.
export type PlacementConfig = {
  id: string; // placement_id
  dashboard_id: string;
  widget_key: string;
  widget_filters: Record<string, unknown>;
  widget_config: Record<string, unknown>;
};

export type TableConfig = {
  default_sort?: {column: string; direction: 'asc' | 'desc'};
};

// Chart styles supported by UniversalChart (mirrors the design system).
export type ChartStyle =
  | 'column'
  | 'line'
  | 'stackedBar'
  | 'spider'
  | 'donut'
  | 'funnel'
  | 'sankey';

export type ChartAxis = {
  key: string;
  label?: string;
  type?: 'date' | 'category' | 'number';
  field_type?: 'dimension' | 'measure';
};

export type ChartSeries = {
  key: string;
  label: string;
  field_type?: 'dimension' | 'measure';
};

// Chart config from the backend. See plans/byod-chart-config-contract.md.
// x_axis may arrive as a single object or an array depending on the endpoint.
export type ChartConfig = {
  default_chart_style?: ChartStyle;

  x_axis?: ChartAxis | ChartAxis[];
  y_axis?: ChartSeries[];
  label_type?: string;
};

export type WidgetDefSummary = {
  id: string;
  name: string;
  widget_type: ApiWidgetType;
  product_area: string | null;
  widget_icon: string | null;
  supported_filters: string[];
  supported_config: string[];
  table_config?: TableConfig | null;
  chart_config?: ChartConfig | null;
};

// Placement with embedded widget definition (GET /dashboards/:id and GET /dashboards/draft)
export type PlacementWithDef = PlacementConfig & {
  widget_def: WidgetDefSummary | null;
  // selected_columns only present in GET /dashboards/:id, not in draft
  selected_columns?: string[] | null;
};

export type DashboardDetail = {
  id: string;
  name: string;
  description: string;
  status: DashboardStatus;
  is_default: boolean;
  global_filters: Record<string, string | string[]>;
  layouts: Layout; // Row[] blob — stored/returned as-is by backend
  widgets_config: Record<string, PlacementWithDef>;
  created_by: UserRef | null;
  updated_by: UserRef | null;
  time_created: string | null;
  time_updated?: string | null;
};

// ---------------------------------------------------------------------------
// Preview / Query responses
// ---------------------------------------------------------------------------

export type PreviewResponseKpi = {
  widget_type: 'kpi';
  value: number | string | null;
};

export type PreviewResponseTable = {
  widget_type: 'table';
  columns: {key: string; label: string}[];
  rows: Record<string, unknown>[];
  table_config?: TableConfig | null;
  pagination: {total_count: number; page_size: number; offset: number};
  // Only present on query results (POST /dashboards/:id/query), not on preview
  selected_columns?: string[] | null;
};

export type PreviewResponseChart = {
  widget_type: 'chart';
  chart_config: ChartConfig | null;
  data: Record<string, unknown>[];
};

export type PreviewResponse =
  | PreviewResponseKpi
  | PreviewResponseTable
  | PreviewResponseChart;

export type QueryWidgetResult =
  | ({placement_id: string; status: 'ok'} & PreviewResponse)
  | {placement_id: string; status: 'error'; error: string};

// Query request — UI sends everything pre-merged per widget
export type QueryWidgetInput = {
  placement_id: string;
  widget_key: string;
  widget_filters: Record<string, string | string[]>;
  widget_config: Record<string, unknown>;
};

export type DashboardQueryRequest = {
  widgets: QueryWidgetInput[];
};

export type DashboardQueryResponse = {
  dashboard_id: string;
  name: string;
  widgets: QueryWidgetResult[];
};

// ---------------------------------------------------------------------------
// Default dashboard
// ---------------------------------------------------------------------------

export type SetDefaultResponse = {dashboard_id: string; is_default: boolean};

// ---------------------------------------------------------------------------
// Publish dashboard
// ---------------------------------------------------------------------------

export type PublishDashboardResponse = {
  dashboard_id: string;
  name: string;
  status: 'published';
  global_filters: Record<string, string | string[]>;
};

// ---------------------------------------------------------------------------
// Update dashboard
// ---------------------------------------------------------------------------

export type WidgetConfigPayload = {
  widget_key: string;
  widget_filters: Record<string, unknown>;
  widget_config: Record<string, unknown>;
};

export type UpdateDashboardResponse = {
  id: string;
  name: string;
  global_filters: Record<string, string | string[]>;
  layout: Layout;
  updated_by: {id: string; name: string};
};

// ---------------------------------------------------------------------------
// Update widget
// ---------------------------------------------------------------------------

export type UpdateWidgetResponse = {
  placement_id: string;
  widget_key: string;
  widget_filters: Record<string, unknown>;
  widget_config: Record<string, unknown>;
  updated_by: {id: string; name: string};
};
